import React from 'react';
import { useAuth } from '../../../context/AuthContext'; // Asegúrate que la ruta sea correcta
import {
    Users,          // Clientes Únicos
    User,           // Género / Clientes 1 Visita
    UserCheck,      // Clientes Recurrentes
    ClipboardList,  // Visitas Totales
    Award,          // Puntos Otorgados
    TrendingUp,     // Cabecera
    AlertTriangle,  // Error
    Info,           // Tooltips
    Clock,          // Última actualización
    Percent,        // Tasa de Retención
    DollarSign      // ✅ Icono para Cashback
} from 'lucide-react';
import { useBusinessMetrics } from '../../../hooks/useBusinessMetrics'; // Asegúrate que la ruta sea correcta
import DateTimeDisplay from '../../../components/common/DateTimeDisplay'; // Asegúrate que la ruta sea correcta
import CountUp from '../../../components/ui/CountUp';
import EmptyState from '../../../components/ui/EmptyState';
import { Skeleton, SkeletonCards } from '../../../components/ui/Skeleton';
import { detectLoyaltyModel } from '../../analytics/utils/loyaltyModel';

const BusinessSnapshotPanel: React.FC = () => {
    const { currentUser } = useAuth();
    
    // ✅ 1. Detección de Modo de Lealtad
    const loyaltyMode = currentUser?.loyaltyMode || 'visits';

    // Usamos el hook que ya debería tener las métricas actualizadas
    const { metrics, loading, error } = useBusinessMetrics(
        currentUser?.businessId,
        currentUser?.timeZone // timeZone no se usa en el hook actual pero se mantiene la llamada
    );

    // Función de formato para números enteros
    const formatNumber = (num: number | undefined): string => {
        if (typeof num !== 'number' || isNaN(num)) return '0';
        return new Intl.NumberFormat('es-MX').format(Math.floor(num)); // Aseguramos entero para visitas
    };

    // ✅ Nueva función para formato de moneda
    const formatCurrency = (num: number | undefined): string => {
        if (typeof num !== 'number' || isNaN(num)) return '$0.00';
        return new Intl.NumberFormat('es-MX', { 
            style: 'currency', 
            currency: 'MXN',
            minimumFractionDigits: 2 
        }).format(num);
    };

    // Función de formato para porcentaje
    const formatPercentage = (num: number | undefined): string => {
        if (typeof num !== 'number' || isNaN(num)) {
            return '0%';
        }
        // Asumiendo que el número ya es el porcentaje (ej. 6.25), lo formateamos
        const result = `${num.toFixed(1)}%`; // Muestra 1 decimal
        return result;
    };

    // 1. Estado de Carga
    if (loading) {
        return (
            <div className="bg-surface overflow-hidden shadow rounded-lg divide-y divide-line">
                <div className="px-4 py-5 sm:px-6">
                    <Skeleton className="h-5 w-48" />
                    <Skeleton className="mt-2 h-3 w-64" />
                </div>
                <div className="px-4 py-5 sm:p-6">
                    <SkeletonCards count={6} />
                </div>
            </div>
        );
    }

    // 2. Estado de Error
    if (error) {
        return (
            <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 shadow rounded-lg p-6 flex flex-col justify-center items-center text-center min-h-[200px]">
                <AlertTriangle className="h-8 w-8 mb-2 text-red-500" />
                <p className="font-medium text-red-700 dark:text-red-300">Error al cargar el resumen</p>
                <p className="text-sm mt-1 text-red-600 dark:text-red-400">{error}</p>
                 {/* Opcional: mostrar el error interno del objeto metrics si existe */}
                 {metrics?.error && <p className="text-xs mt-2 text-red-500 italic">Detalle: {metrics.error}</p>}
            </div>
        );
    }

    // 3. Estado Vacío (cuando no hay error pero no hay clientes)
    // Usamos metrics?.uniqueCustomers para evitar error si metrics es null/undefined inicialmente
    if (!metrics || metrics.uniqueCustomers === 0) {
        return (
            <div className="bg-surface overflow-hidden shadow rounded-lg divide-y divide-line">
                <div className="px-4 py-5 sm:px-6">
                    <h3 className="text-lg font-medium text-content flex items-center">
                        <TrendingUp className="mr-2 h-5 w-5 text-blue-500" />
                        Resumen del Negocio
                    </h3>
                </div>
                <div className="px-4 py-8 sm:p-10 min-h-[200px] flex items-center justify-center">
                    <EmptyState
                        icon={Users}
                        title="¡Empieza a registrar!"
                        subtitle="Aún no hay clientes o visitas registradas para generar un resumen."
                    />
                </div>
            </div>
        );
    }

    // Detectar el modelo de uso para adaptar la lectura de las métricas de visita.
    const modelInfo = detectLoyaltyModel({
        totalVisits: metrics.totalVisits,
        totalPointsGiven: metrics.totalPointsGiven,
        loyaltyMode: currentUser?.loyaltyMode,
    });
    const esPuntosCanje = modelInfo.model === 'puntos-canje';

    // 4. Estado con Datos - Renderizado del Panel Completo
    return (
        <div className="bg-surface overflow-hidden shadow rounded-lg divide-y divide-line">
            {/* Cabecera del Panel */}
            <div className="px-4 py-5 sm:px-6">
                <h3 className="text-lg font-medium text-content flex items-center">
                    <TrendingUp className="mr-2 h-5 w-5 text-blue-500" />
                    Resumen del Negocio
                </h3>
                <p className="mt-1 text-sm text-content-3">
                    Métricas clave actualizadas automáticamente.
                </p>
            </div>

            {/* Contenido Principal - Rejilla de Métricas */}
            <div className="px-4 py-5 sm:p-6">
                {/* Aviso para negocios de modelo "puntos-canje": las métricas de visita son informativas */}
                {esPuntosCanje && (
                    <div className="mb-5 flex items-start gap-2 rounded-lg bg-fuchsia-50 dark:bg-fuchsia-500/10 border border-fuchsia-200 dark:border-fuchsia-500/20 px-3.5 py-2.5">
                        <Info className="h-4 w-4 mt-0.5 text-fuchsia-600 dark:text-fuchsia-400 flex-shrink-0" />
                        <p className="text-xs text-fuchsia-800 dark:text-fuchsia-300">
                            Tu programa funciona por <b>puntos para canje</b>, no por visitas. Las métricas de visitas de abajo son informativas y no reflejan la lealtad real — fíjate mejor en los canjes y puntos otorgados.
                        </p>
                    </div>
                )}
                {/* Rejilla ajustada a 3 columnas en pantallas grandes */}
                <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2 lg:grid-cols-3">

                    {/* Métrica 1: Clientes Únicos */}
                    <div className="sm:col-span-1 bg-blue-50 dark:bg-blue-500/10 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                        <dt className="text-sm font-medium text-accent truncate flex items-center" title="Número total de clientes distintos registrados.">
                            <Users className="mr-1.5 h-4 w-4 text-blue-500" />
                            Clientes Únicos Totales
                            <Info className="ml-1 h-3 w-3 text-content-3 cursor-help" />
                        </dt>
                        <dd className="mt-1 text-3xl font-display font-semibold text-blue-900 dark:text-blue-300">
                            <CountUp value={metrics.uniqueCustomers} format={formatNumber} />
                        </dd>
                        {/* Desglose por género */}
                        <div className="flex items-center mt-2 space-x-3 text-xs text-content-2">
                           <div className="flex items-center" title={`Masculinos: ${formatNumber(metrics.maleCustomers)}`}>
                                <User className="h-4 w-4 text-blue-500 mr-1" />
                                <span>{formatNumber(metrics.maleCustomers)}</span>
                           </div>
                           <div className="flex items-center" title={`Femeninos: ${formatNumber(metrics.femaleCustomers)}`}>
                                <User className="h-4 w-4 text-pink-500 mr-1" />
                                <span>{formatNumber(metrics.femaleCustomers)}</span>
                            </div>
                             {/* Opcional: Mostrar Género Desconocido si es > 0 */}
                             {metrics.unknownGenderCustomers > 0 && (
                               <div className="flex items-center" title={`Otro/Desc.: ${formatNumber(metrics.unknownGenderCustomers)}`}>
                                    <User className="h-4 w-4 text-content-3 mr-1" />
                                    <span>{formatNumber(metrics.unknownGenderCustomers)}</span>
                               </div>
                             )}
                        </div>
                    </div>

                    {/* Métrica 2: Visitas Totales */}
                    <div className="sm:col-span-1 bg-green-50 dark:bg-green-500/10 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                        <dt className="text-sm font-medium text-green-600 dark:text-green-400 truncate flex items-center" title="Número total de registros de visitas (check-ins) acumulados.">
                            <ClipboardList className="mr-1.5 h-4 w-4 text-green-500" />
                            Visitas Totales Globales
                            <Info className="ml-1 h-3 w-3 text-content-3 cursor-help" />
                        </dt>
                        <dd className="mt-1 text-3xl font-display font-semibold text-green-900 dark:text-green-300">
                            <CountUp value={metrics.totalVisits} format={formatNumber} />
                        </dd>
                    </div>

                    {/* ✅ Métrica 3: Puntos Otorgados / Cashback Generado */}
                    <div className="sm:col-span-1 bg-purple-50 dark:bg-purple-500/10 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                        <dt className="text-sm font-medium text-purple-600 dark:text-purple-400 truncate flex items-center" title={loyaltyMode === 'cashback' ? "Dinero total acumulado por los clientes." : "Suma total de puntos ganados por los clientes."}>
                            {loyaltyMode === 'cashback' ? (
                                <DollarSign className="mr-1.5 h-4 w-4 text-purple-500" />
                            ) : (
                                <Award className="mr-1.5 h-4 w-4 text-purple-500" />
                            )}
                            {loyaltyMode === 'cashback' ? 'Cashback Generado' : 'Puntos Otorgados'}
                            <Info className="ml-1 h-3 w-3 text-content-3 cursor-help" />
                        </dt>
                        <dd className="mt-1 text-3xl font-display font-semibold text-purple-900 dark:text-purple-300">
                            <CountUp
                                value={metrics.totalPointsGiven}
                                format={loyaltyMode === 'cashback' ? formatCurrency : formatNumber}
                            />
                        </dd>
                    </div>

                    {/* Métrica 4: Clientes Recurrentes */}
                    <div className="sm:col-span-1 bg-yellow-50 dark:bg-yellow-500/10 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                        <dt className="text-sm font-medium text-yellow-700 dark:text-yellow-300 truncate flex items-center" title="Número de clientes que han registrado 2 O MÁS visitas.">
                            <UserCheck className="mr-1.5 h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                            Clientes Recurrentes
                            <Info className="ml-1 h-3 w-3 text-content-3 cursor-help" />
                        </dt>
                        <dd className="mt-1 text-3xl font-display font-semibold text-yellow-900 dark:text-yellow-300">
                            <CountUp value={metrics.multiVisitClients} format={formatNumber} />
                        </dd>
                    </div>

                    {/* Métrica 5: Clientes con 1 Visita */}
                      <div className="sm:col-span-1 bg-orange-50 dark:bg-orange-500/10 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                        <dt className="text-sm font-medium text-orange-700 dark:text-orange-300 truncate flex items-center" title="Número de clientes que han registrado EXACTAMENTE UNA visita.">
                            <User className="mr-1.5 h-4 w-4 text-orange-600 dark:text-orange-400" />
                            Clientes con 1 Visita
                            <Info className="ml-1 h-3 w-3 text-content-3 cursor-help" />
                        </dt>
                        <dd className="mt-1 text-3xl font-display font-semibold text-orange-900 dark:text-orange-300">
                            <CountUp value={metrics.oneVisitClients} format={formatNumber} />
                        </dd>
                    </div>

                    {/* Métrica 6: Tasa de Retención */}
                    <div className="sm:col-span-1 bg-indigo-50 dark:bg-indigo-500/10 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                        <dt className="text-sm font-medium text-indigo-700 dark:text-indigo-300 truncate flex items-center" title="De los clientes que visitaron (1+ veces), porcentaje que regresó para 2+ visitas.">
                            <Percent className="mr-1.5 h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                            Tasa de Retención
                            <Info className="ml-1 h-3 w-3 text-content-3 cursor-help" />
                        </dt>
                        <dd className="mt-1 text-3xl font-display font-semibold text-indigo-900 dark:text-indigo-300">
                            <CountUp value={metrics.retentionRate} format={formatPercentage} />
                        </dd>
                    </div>

                </dl>

                {/* Última Actualización */}
                {/* Asegurarse que metrics y metrics.lastStatsUpdate existan antes de intentar crear Date */}
                {metrics?.lastStatsUpdate && (
                    <div className="mt-6 text-xs text-content-3 flex items-center justify-end">
                        <Clock className="h-3 w-3 mr-1" />
                        Última act. de datos:
                        <DateTimeDisplay
                            // Crear el objeto Date solo si lastUpdated es válido
                            date={typeof metrics.lastStatsUpdate === 'string' || metrics.lastStatsUpdate instanceof Date ? new Date(metrics.lastStatsUpdate) : new Date()}
                            format="relativeOrShort"
                            showTimeZoneInfo={false}
                            className="ml-1"
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default BusinessSnapshotPanel;